import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Menu, X, Globe, LogOut, User, BarChart3, ChevronDown, ArrowRight, Settings, Shield,
  FileText, FileSpreadsheet, ShieldCheck, RefreshCw, Server, Layers, Calculator,
  BookOpen, Compass, Target, Film, Megaphone, Activity,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { solutionMenuItems } from '@/data/solutions/registry';
const LOGO_URL = '/taxera-logo-transparent.png';
const languages = [
  { code: 'en', short: 'EN' },
  { code: 'de', short: 'DE' },
  { code: 'es', short: 'ES' },
];
/* ─── Menu content: edit here to change the menu everywhere ─── */
const SOLUTION_ITEMS = [
  { href: '/solutions/e-invoicing', label: 'e-Invoicing', desc: 'Rules, deadlines and formats, country by country', icon: FileText },
  { href: '/solutions/saf-t', label: 'SAF-T', desc: 'Audit files straight from your ERP, country by country', icon: FileSpreadsheet },
  { href: '/solutions/vat-returns', label: 'VAT Returns', desc: 'Prepared from your ERP data, country by country', icon: BarChart3 },
  ...solutionMenuItems().map((m) => ({ ...m, icon: FileText })),
  { href: '/solutions/ec-sales-list', label: 'EC Sales List', desc: 'Reports of cross-border sales to EU businesses', icon: FileText },
  { href: '/solutions/intrastat', label: 'Intrastat', desc: 'Reports of goods moving between EU countries', icon: Layers },
  { href: '/solutions/ecompliance', label: 'eCompliance', desc: 'Tax control framework with drill-down to the document', icon: ShieldCheck },
  { href: '/solutions/tax-finance-operations', label: 'Tax & Finance Operations', desc: 'Reconciliations, month-end checks and the tax calendar', icon: RefreshCw },
];
const SAP_ITEMS = [
  { href: '/sap-s4hana', label: 'SAP S/4HANA tax migration', desc: 'Why tax is the forgotten workstream', icon: Server },
  { href: '/sap-s4hana#journey', label: 'Programme journey', desc: 'Taxera in every phase of your migration', icon: Layers },
  { href: '/sap-s4hana/assessment', label: 'Readiness assessment', desc: 'Score your tax readiness for S/4HANA', icon: BarChart3 },
  { href: '/sap-s4hana/calculator', label: 'TCO calculator', desc: 'Total cost of ownership, side by side', icon: Calculator },
  { href: '/sap-s4hana/whitepaper', label: 'Whitepaper', desc: 'Tax in the S/4HANA landscape', icon: FileText },
];
const RESOURCE_ITEMS = [
  { href: '/blog', label: 'Blog', desc: 'Expert analysis and regulatory updates', icon: BookOpen },
  { href: '/news', label: 'News', desc: 'Industry developments and Taxera news', icon: Compass },
  { href: '/mandates', label: 'Mandates', desc: 'Deep dives into specific tax mandates', icon: Target },
  { href: '/knowledge-base', label: 'Country guides', desc: 'Jurisdiction-specific rules and deadlines', icon: Globe },
  { href: '/resources/videos', label: 'Videos', desc: 'Brand and product film library', icon: Film },
  { href: '/media', label: 'Media', desc: 'Press releases and announcements', icon: Megaphone },
];
const TOOL_ITEMS = [
  { href: '/calculator', label: 'Compliance cost calculator', desc: 'What is it really costing you?', icon: Calculator },
  { href: '/evaluate', label: 'Compliance diagnostic', desc: 'Where does your tax function stand?', icon: Activity },
  { href: '/evaluate/business-case', label: 'Business case builder', desc: 'Build an ROI case for stakeholders', icon: FileText },
  { href: '/evaluate/benchmarks', label: 'Industry benchmarks', desc: 'See how you compare', icon: BarChart3 },
];
/* ─── Desktop pieces ─── */
const MegaLink = ({ href, label, desc, icon: Icon, onClick }) => (
  <Link
    to={href}
    onClick={onClick}
    className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
    data-testid={`nav-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
  >
    <div className="w-9 h-9 rounded-lg bg-slate-100 group-hover:bg-taxera-teal/10 flex items-center justify-center shrink-0 transition-colors">
      <Icon className="w-[18px] h-[18px] text-slate-500 group-hover:text-taxera-teal transition-colors" strokeWidth={1.5} />
    </div>
    <div>
      <div className="text-[14px] font-semibold text-taxera-charcoal group-hover:text-taxera-blue transition-colors leading-tight">{label}</div>
      <div className="text-[12px] text-slate-400 leading-snug mt-0.5">{desc}</div>
    </div>
  </Link>
);
const SectionLabel = ({ children }) => (
  <div className="text-[11px] font-bold tracking-[0.15em] uppercase text-slate-300 mb-2 px-3">{children}</div>
);
/* ─── Mobile overlay ─── */
const MobileSection = ({ id, label, open, onToggle, children }) => (
  <div className="border-b border-slate-100" data-testid={`mobile-section-${id}`}>
    <button onClick={() => onToggle(id)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`mobile-toggle-${id}`}>
      <span className="text-base font-semibold text-taxera-charcoal">{label}</span>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
    </button>
    {open && <div className="px-2 pb-4 space-y-0.5">{children}</div>}
  </div>
);
const MobileLink = ({ item, onClose }) => (
  <Link to={item.href} onClick={onClose} className="flex items-center gap-3 px-4 py-2.5 rounded-xl hover:bg-slate-50">
    <item.icon className="w-4 h-4 text-slate-400" strokeWidth={1.5} />
    <div>
      <div className="text-sm font-semibold text-taxera-charcoal">{item.label}</div>
      {item.desc && <div className="text-xs text-slate-400">{item.desc}</div>}
    </div>
  </Link>
);
/* ─── Header ─── */
const Header = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, adminStatus } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openMobile, setOpenMobile] = useState(null);
  const menuTimeout = useRef(null);
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    setIsOverlayOpen(false);
    setOpenMenu(null);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = isOverlayOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOverlayOpen]);
  const enter = (id) => { clearTimeout(menuTimeout.current); setOpenMenu(id); };
  const leave = () => { menuTimeout.current = setTimeout(() => setOpenMenu(null), 180); };
  const close = () => setOpenMenu(null);
  const closeAll = () => { setIsOverlayOpen(false); setOpenMobile(null); };
  const current = languages.find((l) => l.code === i18n.language) || languages[0];
  const triggerClass = (active) =>
    `px-3 py-2 text-[15px] font-semibold rounded-lg transition-colors whitespace-nowrap ${active ? 'text-taxera-blue' : 'text-taxera-charcoal hover:text-taxera-blue'}`;
  const renderDropdown = (id, label, width, children) => (
    <div key={id} className="relative" onMouseEnter={() => enter(id)} onMouseLeave={leave}>
      <button
        className={`${triggerClass(openMenu === id)} inline-flex items-center gap-1`}
        onClick={() => setOpenMenu(openMenu === id ? null : id)}
        aria-expanded={openMenu === id}
        data-testid={`nav-${id}`}
      >
        {label} <ChevronDown className="w-3.5 h-3.5 opacity-60" />
      </button>
      <AnimatePresence>
        {openMenu === id && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 pt-3 z-50"
            data-testid={`dropdown-${id}`}
          >
            <div className={`bg-white rounded-2xl border border-slate-200 shadow-2xl p-4 ${width}`}>{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100/50' : 'bg-white/60 backdrop-blur-md'
        }`}
      >
        <div className="w-full px-6 lg:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src={LOGO_URL} alt="Taxera" className="h-12 md:h-[3.4rem] w-auto object-contain" />
            </Link>
            {/* Desktop navigation: five items */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              <Link to="/platform" className={triggerClass(location.pathname === '/platform')} data-testid="nav-platform">
                {t('nav.platform', 'Platform')}
              </Link>
              {renderDropdown('solutions', t('nav.solutions', 'Solutions'), 'w-[680px]', (<>
                <div className="grid grid-flow-col grid-cols-2 gap-x-2 gap-y-0.5" style={{ gridTemplateRows: `repeat(${Math.ceil(SOLUTION_ITEMS.length / 2)}, minmax(0, auto))` }}>
                  {SOLUTION_ITEMS.map((item) => <MegaLink key={item.label} {...item} onClick={close} />)}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-100 px-3">
                  <Link to="/products" onClick={close} className="inline-flex items-center gap-2 text-xs font-semibold text-taxera-blue hover:text-taxera-charcoal transition-colors group" data-testid="nav-all-solutions">
                    See all solutions <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                  </Link>
                </div>
              </>))}
              <Link to="/tracker" className={triggerClass(location.pathname.startsWith('/tracker'))} data-testid="nav-tracker">
                Global Tax Tracker
              </Link>
              {renderDropdown('sap', 'SAP', 'w-[420px]', (
                <div className="space-y-0.5">
                  {SAP_ITEMS.map((item) => <MegaLink key={item.label} {...item} onClick={close} />)}
                </div>
              ))}
              {renderDropdown('resources', t('nav.resources', 'Resources'), 'w-[640px]', (
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <SectionLabel>Learn</SectionLabel>
                    <div className="space-y-0.5">
                      {RESOURCE_ITEMS.map((item) => <MegaLink key={item.label} {...item} onClick={close} />)}
                    </div>
                  </div>
                  <div>
                    <SectionLabel>Tools</SectionLabel>
                    <div className="space-y-0.5">
                      {TOOL_ITEMS.map((item) => <MegaLink key={item.label} {...item} onClick={close} />)}
                    </div>
                  </div>
                </div>
              ))}
            </nav>
            {/* Right side */}
            <div className="flex items-center gap-2">
              <div className="relative hidden lg:block" onMouseEnter={() => enter('lang')} onMouseLeave={leave}>
                <button className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-taxera-charcoal hover:bg-slate-50 transition-colors" data-testid="language-indicator">
                  <Globe className="w-3.5 h-3.5" strokeWidth={1.5} /> {current.short}
                </button>
                <AnimatePresence>
                  {openMenu === 'lang' && (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }} className="absolute top-full right-0 pt-3">
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-3 min-w-[140px]" data-testid="dropdown-lang">
                        <div className="grid grid-cols-2 gap-1">
                          {languages.map((lang) => (
                            <button
                              key={lang.code}
                              onClick={() => { i18n.changeLanguage(lang.code); document.documentElement.lang = lang.code; setOpenMenu(null); }}
                              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors text-center ${i18n.language === lang.code ? 'bg-taxera-blue text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-taxera-charcoal'}`}
                              data-testid={`lang-${lang.code}`}
                            >
                              {lang.short}
                            </button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              {!user && (
                <Link to="/login" className="hidden lg:block text-sm font-medium text-taxera-charcoal/70 hover:text-taxera-blue transition-colors px-2" data-testid="login-link">
                  {t('nav.login', 'Login')}
                </Link>
              )}
              {user && (
                <div className="relative hidden lg:block" onMouseEnter={() => enter('user')} onMouseLeave={leave}>
                  <button className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors" data-testid="user-menu-btn">
                    {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-taxera-blue/10 flex items-center justify-center"><User className="w-3.5 h-3.5 text-taxera-blue" /></div>}
                    <span className="text-xs font-medium text-taxera-charcoal max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
                  </button>
                  <AnimatePresence>
                    {openMenu === 'user' && (
                      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} transition={{ duration: 0.15 }} className="absolute top-full right-0 pt-3">
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-3 min-w-[180px]" data-testid="dropdown-user">
                          <div className="px-3 py-2 mb-1"><div className="text-sm font-medium text-taxera-charcoal">{user.name}</div><div className="text-xs text-slate-400">{user.email}</div></div>
                          <div className="space-y-0.5">
                            <Link to="/dashboard" onClick={close} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-sm text-taxera-charcoal"><BarChart3 className="w-4 h-4 text-slate-400" /> {t('nav.dashboard', 'Dashboard')}</Link>
                            <Link to="/settings" onClick={close} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-sm text-taxera-charcoal"><Settings className="w-4 h-4 text-slate-400" /> {t('nav.settings', 'Settings')}</Link>
                            {adminStatus?.isAdmin && <Link to="/admin" onClick={close} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-sm text-taxera-charcoal"><Shield className="w-4 h-4 text-amber-500" /> {t('nav.admin', 'Admin Portal')}</Link>}
                            <button onClick={() => { logout(); close(); navigate('/'); }} className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm text-red-600 w-full text-left"><LogOut className="w-4 h-4" /> {t('nav.logout', 'Logout')}</button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              <Link to="/contact" className="hidden sm:inline-flex px-5 py-2.5 taxera-gradient text-white rounded-full text-sm font-medium hover:shadow-lg transition-all hover:scale-105" data-testid="request-demo-btn">
                {t('common.bookDemo', 'Book a Demo')}
              </Link>
              <button onClick={() => setIsOverlayOpen(!isOverlayOpen)} className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="nav-menu-btn" aria-label="Menu">
                <Menu className="w-5 h-5 text-taxera-charcoal" />
              </button>
            </div>
          </div>
        </div>
      </header>
      {/* Mobile overlay */}
      <AnimatePresence>
        {isOverlayOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 z-[60] bg-white overflow-y-auto" data-testid="nav-overlay">
            <div className="flex items-center justify-between px-5 h-16 border-b border-slate-100 sticky top-0 bg-white z-10">
              <Link to="/" onClick={closeAll} className="flex items-center" data-testid="overlay-logo">
                <img src={LOGO_URL} alt="Taxera" className="h-10 w-auto object-contain" />
              </Link>
              <button onClick={closeAll} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-100 transition-colors" data-testid="overlay-close-btn" aria-label="Close menu">
                <X className="w-5 h-5 text-taxera-charcoal" />
              </button>
            </div>
            <div>
              <Link to="/platform" onClick={closeAll} className="block px-5 py-4 text-base font-semibold text-taxera-charcoal border-b border-slate-100" data-testid="mobile-platform">
                {t('nav.platform', 'Platform')}
              </Link>
              <MobileSection id="solutions" label={t('nav.solutions', 'Solutions')} open={openMobile === 'solutions'} onToggle={(id) => setOpenMobile(openMobile === id ? null : id)}>
                {SOLUTION_ITEMS.map((item) => <MobileLink key={item.label} item={item} onClose={closeAll} />)}
                <MobileLink item={{ href: '/products', label: 'See all solutions', icon: ArrowRight }} onClose={closeAll} />
              </MobileSection>
              <Link to="/tracker" onClick={closeAll} className="block px-5 py-4 text-base font-semibold text-taxera-charcoal border-b border-slate-100" data-testid="mobile-tracker">
                Global Tax Tracker
              </Link>
              <MobileSection id="sap" label="SAP" open={openMobile === 'sap'} onToggle={(id) => setOpenMobile(openMobile === id ? null : id)}>
                {SAP_ITEMS.map((item) => <MobileLink key={item.label} item={item} onClose={closeAll} />)}
              </MobileSection>
              <MobileSection id="resources" label={t('nav.resources', 'Resources')} open={openMobile === 'resources'} onToggle={(id) => setOpenMobile(openMobile === id ? null : id)}>
                {RESOURCE_ITEMS.map((item) => <MobileLink key={item.label} item={item} onClose={closeAll} />)}
                {TOOL_ITEMS.map((item) => <MobileLink key={item.label} item={item} onClose={closeAll} />)}
              </MobileSection>
            </div>
            <div className="px-5 py-4 border-t border-slate-100">
              {user ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-3 px-2 py-2">
                    {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-taxera-blue/10 flex items-center justify-center"><User className="w-4 h-4 text-taxera-blue" /></div>}
                    <div><div className="text-sm font-medium text-taxera-charcoal">{user.name}</div><div className="text-xs text-slate-400">{user.email}</div></div>
                  </div>
                  <Link to="/dashboard" onClick={closeAll} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 text-sm text-taxera-charcoal"><BarChart3 className="w-4 h-4 text-slate-400" /> My Diagnostics</Link>
                  <Link to="/settings" onClick={closeAll} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 text-sm text-taxera-charcoal"><Settings className="w-4 h-4 text-slate-400" /> Settings</Link>
                  {adminStatus?.isAdmin && <Link to="/admin" onClick={closeAll} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-slate-50 text-sm text-taxera-charcoal"><Shield className="w-4 h-4 text-amber-500" /> Admin Portal</Link>}
                  <button onClick={() => { logout(); closeAll(); navigate('/'); }} className="flex items-center gap-3 px-2 py-2.5 rounded-xl hover:bg-red-50 text-sm text-red-600 w-full text-left"><LogOut className="w-4 h-4" /> {t('common.signOut', 'Sign out')}</button>
                </div>
              ) : (
                <Link to="/login" onClick={closeAll} className="flex items-center justify-center gap-2 w-full py-3 border border-slate-200 rounded-xl text-sm font-semibold text-taxera-charcoal hover:bg-slate-50 transition-colors" data-testid="mobile-login-btn">
                  <User className="w-4 h-4" /> {t('common.signIn', 'Sign in')}
                </Link>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-400" />
                <div className="flex gap-1.5">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => { i18n.changeLanguage(lang.code); document.documentElement.lang = lang.code; }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${i18n.language === lang.code ? 'bg-taxera-blue text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      data-testid={`mobile-lang-${lang.code}`}
                    >
                      {lang.short}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-5 pb-8">
              <Link to="/contact" onClick={closeAll} className="flex items-center justify-center gap-2 w-full py-3.5 taxera-gradient text-white rounded-full font-semibold text-sm hover:shadow-xl transition-all" data-testid="overlay-request-demo">
                {t('common.bookDemo', 'Book a Demo')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
export default Header;
